import { StandardEvents } from '@shopify/events';

class BTRestockInquiry extends HTMLElement {
  #section;
  #controller;
  #version = 0;
  #pending = false;
  #opener;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#pending = false;
    const options = { signal: this.#controller.signal };
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onSelect, options);
    this.addEventListener('click', this.#onClick, options);
    this.querySelector('dialog')?.addEventListener('close', () => {
      if (this.#opener?.isConnected) this.#opener.focus();
    }, options);
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, options);
    this.#sync();

    // Shopify, not the client, determines whether the form succeeded.
    const form = this.querySelector('form');
    const result = this.querySelector('[data-result]');
    let ownSubmission = location.hash === `#${form?.id}`;
    try { ownSubmission ||= sessionStorage.getItem('bt-restock-submission') === form?.id; } catch {}
    if (result && ownSubmission) {
      this.#setSuccessState(result.dataset.result === 'success');
      const dialog = this.querySelector('dialog');
      if (!dialog.open) dialog.showModal();
      result.focus();
      try { sessionStorage.removeItem('bt-restock-submission'); } catch {}
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#version++;
    const dialog = this.querySelector('dialog');
    if (dialog?.open) dialog.close();
  }

  #sync() {
    const allowed = this.dataset.allowed === 'true';
    this.querySelector('[data-prompt]').hidden = !allowed;
    this.querySelector('[data-open]').disabled = this.#pending || !allowed;
    const submit = this.querySelector('[type="submit"]');
    if (submit) submit.disabled = this.#pending || !allowed;
  }

  #setSuccessState(success) {
    const heading = this.querySelector('[data-heading]');
    const intro = this.querySelector('[data-intro]');
    heading.textContent = success ? 'Your inquiry has been sent' : heading.dataset.formTitle;
    intro.hidden = success;
    this.querySelector('[data-fields]').hidden = success;
    const description = success ? this.querySelector('[data-success-message]') : intro;
    this.querySelector('dialog').setAttribute('aria-describedby', description.id);
  }

  #fillContext() {
    const url = new URL(this.dataset.productUrl, location.origin);
    if (this.dataset.variantId) url.searchParams.set('variant', this.dataset.variantId);
    this.querySelector('[data-product-field]').value = this.dataset.title || '';
    this.querySelector('[data-selection-field]').value = this.dataset.selection || '';
    this.querySelector('[data-selection-row]').hidden = !this.dataset.selection;
    this.querySelector('[data-variant-field]').value = this.dataset.variantId || '';
    this.querySelector('[data-sku-field]').value = this.dataset.sku || '';
    this.querySelector('[data-url-field]').value = url.href;
    const form = this.querySelector('form');
    const returnInput = form.querySelector('[name="return_to"]');
    if (returnInput) returnInput.value = `${url.pathname}${url.search}#${form.id}`;
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const dialog = this.querySelector('dialog');
    if (event.target.closest('[data-close]')) {
      dialog.close();
    } else if (event.target.closest('[data-open]')) {
      if (this.#pending || this.dataset.allowed !== 'true') return;
      const result = this.querySelector('[data-result]');
      if (result?.dataset.result === 'success') {
        this.querySelector('form').reset();
        this.#setSuccessState(false);
        result.remove();
      }
      this.#fillContext();
      this.#opener = event.target.closest('[data-open]');
      if (!dialog.open) dialog.showModal();
      this.querySelector('[type="email"]')?.focus();
    }
  };

  #onSubmit = (event) => {
    if (this.#pending || this.dataset.allowed !== 'true') {
      event.preventDefault();
      return;
    }
    this.#fillContext();
    try { sessionStorage.setItem('bt-restock-submission', event.target.id); } catch {}
    // Keep Shopify's normal submission and spam protection; no AJAX interception.
  };

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const version = ++this.#version;
    this.#pending = true;
    this.#sync();
    const dialog = this.querySelector('dialog');
    if (dialog.open) dialog.close();
    Promise.resolve(event.promise).then((response) => {
      if (!this.isConnected || version !== this.#version) return;
      const detail = response?.detail;
      if (!detail?.html) throw new Error('Missing product update');
      if (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId) {
        this.#pending = false;
        this.#sync();
        return;
      }
      const next = [...detail.html.querySelectorAll('bt-restock-inquiry')]
        .find((element) => element.dataset.blockKey === this.dataset.blockKey);
      if (!next) throw new Error('Missing restock context');
      for (const key of ['allowed', 'productId', 'title', 'selection', 'variantId', 'sku', 'productUrl']) {
        this.dataset[key] = next.dataset[key] || '';
      }
      this.#pending = false;
      this.#sync();
      this.#fillContext();
    }).catch((error) => {
      if (!this.isConnected || version !== this.#version) return;
      // Never permit an inquiry with stale size/fit data after a failed update.
      this.dataset.allowed = 'false';
      this.#pending = false;
      this.#sync();
      if (error?.name !== 'AbortError') console.warn('[restock-inquiry] Product update failed:', error);
    });
  };
}

if (!customElements.get('bt-restock-inquiry')) {
  customElements.define('bt-restock-inquiry', BTRestockInquiry);
}
